import { loginSchema } from "@closer/shared";
import { Button, Card, FormField, Input, useToast } from "@closer/ui";
import { Link, Navigate, useLocation, useNavigate } from "react-router";
import type { z } from "zod";

import { ApiRequestError } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";
import { inviteReturnPath } from "../../lib/inviteReturn";
import { applyServerErrors, useZodForm } from "../../lib/useZodForm";
import { SocialAuthButtons } from "./SocialAuthButtons";

type LoginValues = z.infer<typeof loginSchema>;

export function LoginPage() {
  const { user, isLoading, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const form = useZodForm(loginSchema, {
    defaultValues: { email: "", password: "" } as LoginValues,
  });

  /**
   * Davetten gelindiyse dönüş davet ekranına (CLOS-74 `[035]`); yoksa her zamanki gibi
   * firma seçimine. Erken yönlendirme de aynı hedefi kullanıyor: zaten oturumu olan
   * kullanıcı bağlantıya tıkladığında buraya uğrayıp `/tenants`'a savrulmasın.
   */
  const hedef = inviteReturnPath(location.search) ?? "/tenants";

  if (!isLoading && user) return <Navigate to={hedef} replace />;

  const onSubmit = form.handleSubmit(async (values) => {
    try {
      await login(values);
      navigate(hedef);
    } catch (error) {
      applyServerErrors(form, error);
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : "Giriş yapılamadı. Lütfen tekrar deneyin.",
      );
    }
  });

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 p-4">
      <Card title="Giriş Yap" className="w-full max-w-sm">
        <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
          <FormField label="E-posta" error={form.formState.errors.email?.message} required>
            <Input type="email" autoComplete="email" {...form.register("email")} />
          </FormField>
          <FormField label="Şifre" error={form.formState.errors.password?.message} required>
            <Input type="password" autoComplete="current-password" {...form.register("password")} />
          </FormField>
          <Button type="submit" loading={form.formState.isSubmitting}>
            Giriş Yap
          </Button>
          <SocialAuthButtons label="Google ile devam et" />
          <p className="text-center text-sm text-slate-500">
            <Link to="/forgot-password" className="font-medium text-primary hover:underline">
              Şifremi unuttum
            </Link>
          </p>
          <p className="text-center text-sm text-slate-500">
            Hesabınız yok mu?{" "}
            {/* Davet bilgisi karşı sayfaya da taşınıyor: davetli "kayıt olun"a tıkladığında
                token'ı kaybederse hesabı açar ama davetine dönemezdi. */}
            <Link
              to={`/register${location.search}`}
              className="font-medium text-primary hover:underline"
            >
              Kayıt olun
            </Link>
          </p>
        </form>
      </Card>
    </div>
  );
}
